/**
 * Command Palette Bridge
 *
 * Bridges between our command registry and svelte-command-palette's action format.
 * Converts Command objects to the format expected by defineActions().
 * Supports mode-aware filtering to show only contextually relevant commands.
 */

import { defineActions } from "svelte-command-palette";
import type { action as PaletteAction } from "svelte-command-palette";
import type { InputSystem, CommandInfo } from "@rool/input";
import type { UINode } from "@rool/renderer";
import * as registry from "./registry";
import type { Command } from "./types";
import { MODE_DISPLAY_NAMES } from "./types";

/**
 * Build svelte-command-palette actions from our command registry.
 *
 * @param input - InputSystem for getting shortcut display strings
 * @param selectedNodes - Current selection for dynamic visibility
 */
export function buildPaletteActions(
  input: InputSystem,
  selectedNodes: UINode[],
): PaletteAction[] {
  const commands = registry.getPaletteCommands(selectedNodes);
  const inputCommands = input.getCommands();

  // Build a map from command ID to InputSystem command info for shortcuts
  const shortcutMap = new Map<string, CommandInfo>();
  for (const cmd of inputCommands) {
    shortcutMap.set(cmd.id, cmd);
  }

  // Convert commands to svelte-command-palette action format
  // Note: We intentionally omit `shortcut` from palette actions because
  // svelte-command-palette registers shortcuts globally via tinykeys on window,
  // which conflicts with text inputs. We handle all keyboard shortcuts ourselves
  // via the InputSystem, and show the shortcut display in the subTitle instead.
  const actionDefs: PaletteAction[] = commands.map((cmd) => {
    const inputCmd = shortcutMap.get(cmd.id);

    // Get display label (use dynamic label if available)
    const label = cmd.getLabel
      ? (cmd.getLabel(selectedNodes) as string)
      : cmd.label;

    const shortcutDisplay = inputCmd?.display;
    const subTitle = shortcutDisplay
      ? `${cmd.category} · ${shortcutDisplay}`
      : cmd.category;

    return {
      actionId: cmd.id,
      title: label,
      subTitle,
      description: cmd.description,
      keywords: [cmd.category, cmd.section].filter(Boolean) as string[],
      group: cmd.category,
      onRun: () => {
        // First try executing via InputSystem (for commands with keyboard handlers)
        const executed = input.executeCommand(cmd.id);
        if (!executed) {
          // Fall back to local registry execute
          cmd.execute();
        }
      },
      canActionRun: () => {
        // Check dynamic visibility
        if (cmd.getLabel) {
          return cmd.getLabel(selectedNodes) !== false;
        }
        return true;
      },
    };
  });

  return defineActions(actionDefs);
}

/**
 * Build actions from InputSystem commands with mode-aware filtering.
 * This pulls commands directly from InputSystem rather than our registry.
 *
 * @param input - InputSystem for getting commands and mode state
 * @param showAll - If true, show all commands regardless of mode (grayed out if inactive)
 */
export function buildInputSystemActions(
  input: InputSystem,
  showAll: boolean = false,
): PaletteAction[] {
  const commands = input.getCommands();
  const modeStack = input.getStack();
  const topMode = modeStack.at(-1) ?? "default";

  // Filter by mode unless "show all" is enabled
  const filteredCommands = showAll
    ? commands
    : commands.filter((cmd) => modeStack.includes(cmd.mode));

  // Sort: mode-specific commands first (when not in default mode), then alphabetically
  const sorted = [...filteredCommands].sort((a, b) => {
    // Mode-specific commands come first
    const aIsTopMode = a.mode === topMode && topMode !== "default";
    const bIsTopMode = b.mode === topMode && topMode !== "default";
    if (aIsTopMode && !bIsTopMode) return -1;
    if (!aIsTopMode && bIsTopMode) return 1;
    // Then sort alphabetically by name
    return a.name.localeCompare(b.name);
  });

  const actionDefs: PaletteAction[] = sorted.map((cmd) => {
    const isActive = modeStack.includes(cmd.mode);
    const isModeSpecific = cmd.mode === topMode && topMode !== "default";

    const subTitle = cmd.display
      ? `${cmd.category} · ${cmd.display}`
      : cmd.category;

    return {
      actionId: cmd.id,
      title: cmd.name,
      subTitle,
      keywords: cmd.category ? [cmd.category, cmd.mode] : [cmd.mode],
      // Group mode-specific commands separately
      group: isModeSpecific
        ? MODE_DISPLAY_NAMES[topMode] || topMode
        : cmd.category,
      onRun: () => {
        input.executeCommand(cmd.id);
      },
      // When showing all, inactive commands can't run
      canActionRun: () => isActive,
    };
  });

  return defineActions(actionDefs);
}

/**
 * Get the display name for the current mode (if not default).
 * Returns null if in default mode or no special display name exists.
 */
export function getCurrentModeDisplay(input: InputSystem): string | null {
  const topMode = input.getStack().at(-1) ?? "default";
  if (topMode === "default") return null;
  return MODE_DISPLAY_NAMES[topMode] ?? topMode;
}

/**
 * Get the current mode stack from InputSystem.
 */
export function getModeStack(input: InputSystem): string[] {
  return input.getStack();
}
